import React, { useState, useEffect } from "react";
import { CircularProgress } from "@material-ui/core";
import { WhatsApp } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useFacebookSDK from "../../hooks/useFacebookSDK";

const WhatsAppEmbeddedSignup = ({ onSuccess, disabled }) => {
  const [loading, setLoading] = useState(false);
  const [signupData, setSignupData] = useState(null);
  const [authCode, setAuthCode] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Hook para carregar SDK com o appId específico para WhatsApp Business
  const { sdkLoaded, loading: sdkLoading, loadSDK } = useFacebookSDK(
    process.env.REACT_APP_FACEBOOK_APP_ID_FOR_WHATS_BUSINESS || process.env.REACT_APP_FACEBOOK_APP_ID
  );

  // Effect 2: Listen for WhatsApp Embedded Signup events
  useEffect(() => {
    const handleMessage = (event) => {
      // Valida origem do evento (Facebook)
      if (!event.origin.endsWith('facebook.com')) {
        return;
      }
      
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          console.log('✅ [React] WhatsApp Embedded Signup event:', data);
          
          // Handle successful completion
          if (data.event === 'FINISH' || data.event === 'FINISH_ONLY_WABA' || data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
            const { phone_number_id, waba_id, business_id } = data.data;
            console.log('✅ [React] Signup completed:', {
              event: data.event,
              phone_number_id,
              waba_id,
              business_id
            });
            
            // Armazena globalmente para não perder se o componente desmontar
            window.__whatsappSignupData = data.data;
            
            // Atualiza o state com os dados do signup
            setSignupData(data.data);
          }
          // Handle cancellation
          else if (data.event === 'CANCEL') {
            const { current_step } = data.data;
            console.warn('⚠️ Signup cancelled at step:', current_step);
            toast.warning('Cadastro cancelado');
            setLoading(false);
          }
          // Handle error
          else if (data.event === 'ERROR') {
            const { error_message } = data.data;
            console.error('❌ Signup error:', error_message);
            toast.error(`Erro: ${error_message}`);
            setLoading(false);
          }
        }
      } catch (error) {
        // Non-JSON response
      }
    };

    window.addEventListener('message', handleMessage);
  }, []);

  // Effect 3: Process signup when both authCode and signupData are available
  useEffect(() => {
    if (authCode && signupData && !isProcessing) {
      console.log('🚀 Both authCode and signupData available, processing...');
      setIsProcessing(true);
      processEmbeddedSignup(authCode, signupData);
    }
  }, [authCode, signupData, isProcessing]);

  // Process the complete embedded signup flow
  const processEmbeddedSignup = async (code, data) => {
    try {
      console.log('🔐 Processing embedded signup');
      console.log('📋 Code:', code);
      console.log('📋 Signup data:', data);
      
      if (!data || !data.waba_id) {
        toast.error('Dados do cadastro inválidos.');
        setLoading(false);
        return;
      }
      
      // Exchange code for access token
      console.log('🔄 Exchanging code for access token...');
      const tokenResult = await api.post('/whatsapp-business-api/exchange-token', {
        code: code
      });

      console.log('✅ Token exchange result:', tokenResult.data);
      
      if (!tokenResult.data.access_token) {
        toast.error('Falha ao obter token de acesso');
        setLoading(false);
        return;
      }
      
      // Send to backend
      const response = await api.post('/whatsapp-business-api/embedded-signup', {
        accessToken: tokenResult.data.access_token,
        wabaId: data.waba_id,
        phoneNumberId: data.phone_number_id,
        businessId: data.business_id
      });

      console.log('✅ Backend response:', response.data);
      
      if (response.data.webhookUrl) {
        console.log('🔗 Webhook URL configurado:', response.data.webhookUrl);
      }

      toast.success(`WhatsApp Business conectado: ${response.data.data?.verifiedName || 'Sucesso'}!`);
      
      // Reset states
      setAuthCode(null);
      setSignupData(null);
      setIsProcessing(false);
      window.__whatsappSignupData = null;
      
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      console.error('❌ Error processing embedded signup:', error);
      toastError(error);
      setIsProcessing(false);
    } finally {
      setLoading(false);
    }
  };

  const fbLoginCallback = async (response) => {
    console.log('📞 FB Login callback response:', response);
    
    if (response.authResponse) {
      const code = response.authResponse.code;
      console.log('🔑 Authorization code received:', code);
      
      // Verifica se já tem signupData (pode ter chegado antes)
      let data = signupData || window.__whatsappSignupData;
      
      if (data) {
        console.log('✅ Signup data already available, processing immediately...');
        await processEmbeddedSignup(code, data);
      } else {
        console.log('⏳ Waiting for signup data...');
        // Atualiza o state com o código
        // O useEffect irá processar quando signupData chegar
        setAuthCode(code);
      }
    } else {
      console.log('❌ Login response without authResponse:', response);
      toast.error('Falha na autenticação');
      setLoading(false);
    }
  };

  const launchWhatsAppSignup = async () => {
    if (!process.env.REACT_APP_FACEBOOK_APP_ID_FOR_WHATS_BUSINESS) {
      toast.error('Facebook App ID não configurado');
      return;
    }

    if (!process.env.REACT_APP_WHATSAPP_CONFIG_ID) {
      toast.error('WhatsApp Configuration ID não configurado');
      return;
    }

    console.log('🚀 Launching WhatsApp Embedded Signup...');
    console.log('📋 Config ID:', process.env.REACT_APP_WHATSAPP_CONFIG_ID);
    console.log('📋 App ID:', process.env.REACT_APP_FACEBOOK_APP_ID_FOR_WHATS_BUSINESS);

    // Reset states
    setAuthCode(null);
    setSignupData(null);
    setIsProcessing(false);
    window.__whatsappSignupData = null;
    setLoading(true);

    try {
      // Carregar SDK com o appId correto antes de iniciar
      await loadSDK();

      if (!window.FB) {
        toast.error('Erro ao carregar SDK do Facebook');
        setLoading(false);
        return;
      }

      // Launch Facebook login
      window.FB.login((response) => {
        console.log('📞 FB.login callback triggered');
        fbLoginCallback(response);
      }, {
        config_id: process.env.REACT_APP_WHATSAPP_CONFIG_ID,
        response_type: 'code', // must be set to 'code' for System User access token
        override_default_response_type: true, // when true, any response types passed in the "response_type" will take precedence over the default types
        extras: {
          "setup": {},
          "featureType": "whatsapp_business_app_onboarding",
          "sessionInfoVersion": "3"
        }
      });
    } catch (error) {
      console.error('❌ Error launching WhatsApp signup:', error);
      toast.error('Erro ao iniciar cadastro do WhatsApp');
      setLoading(false);
    }
  };

  return (
    <>
      {loading || sdkLoading ? (
        <CircularProgress size={20} style={{ marginRight: "10px" }} />
      ) : (
        <WhatsApp
          fontSize="small"
          style={{
            marginRight: "10px",
            color: "#25D366",
          }}
        />
      )}
      <span
        onClick={!disabled && !loading && !sdkLoading ? launchWhatsAppSignup : undefined}
        style={{
          cursor: !disabled && !loading && !sdkLoading ? 'pointer' : 'default',
          opacity: disabled || loading || sdkLoading ? 0.5 : 1,
        }}
      >
        {loading || sdkLoading ? 'Carregando SDK...' : 'WhatsApp Business API'}
      </span>
    </>
  );
};

export default WhatsAppEmbeddedSignup;
